import { QueryClient } from '@tanstack/react-query';
import { DataError } from '@/lib/data';

/**
 * Configurazione di TanStack Query.
 *
 * Le impostazioni sono tarate sull'uso in sala: dati freschi ma non ricaricati
 * a ogni cambio di scheda, e nessun tentativo automatico su errori che il retry
 * non può risolvere (permesso negato, conflitto, validazione).
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: true,
      // Alla riconnessione i dati vanno riletti: il realtime non recapita gli
      // eventi persi durante la disconnessione.
      refetchOnReconnect: true,
      retry: (failureCount, error) => {
        if (error instanceof DataError) {
          // Ritentare un 403 o una violazione di vincolo non cambierebbe l'esito.
          if (error.isForbidden || error.isValidation || error.isConflict || error.isOverlap) {
            return false;
          }
        }
        return failureCount < 2;
      },
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    },
    mutations: {
      // Le mutazioni non vengono mai ritentate automaticamente: quelle critiche
      // portano una chiave di idempotenza e il retry è una decisione esplicita
      // dell'utente, non un comportamento implicito del client.
      retry: false,
    },
  },
});

/** Chiavi di cache, centralizzate per invalidare in modo coerente. */
export const queryKeys = {
  context: ['context'] as const,
  locations: (organizationId: string) => ['locations', organizationId] as const,
  platformOrganizations: ['platform', 'organizations'] as const,
  roles: (organizationId: string) => ['roles', organizationId] as const,
  permissionCatalog: ['permission-catalog'] as const,
  members: (organizationId: string) => ['members', organizationId] as const,
  invitations: (organizationId: string) => ['invitations', organizationId] as const,
  floor: (locationId: string) => ['floor', locationId] as const,
  areas: (locationId: string) => ['areas', locationId] as const,
  order: (orderId: string) => ['order', orderId] as const,
  openOrders: (locationId: string) => ['orders', 'open', locationId] as const,
  ordersByDate: (locationId: string, date: string) => ['orders', locationId, date] as const,
  kitchen: (locationId: string) => ['kitchen', locationId] as const,
  menuProducts: (organizationId: string) => ['menu', 'products', organizationId] as const,
  menuCategories: (organizationId: string) => ['menu', 'categories', organizationId] as const,
  availability: (locationId: string) => ['menu', 'availability', locationId] as const,
  allergens: ['allergens'] as const,
  vatRates: (organizationId: string) => ['vat-rates', organizationId] as const,
  reservations: (locationId: string, date: string) => ['reservations', locationId, date] as const,
  upcomingReservations: (locationId: string) => ['reservations', 'upcoming', locationId] as const,
  customers: (organizationId: string) => ['customers', organizationId] as const,
  cashSession: (locationId: string) => ['cash', 'open', locationId] as const,
  cashSessions: (locationId: string) => ['cash', 'sessions', locationId] as const,
  cashMovements: (sessionId: string) => ['cash', 'movements', sessionId] as const,
  tipsOfLocation: (locationId: string) => ['cash', 'tips', locationId] as const,
  tips: (locationId: string, date: string) => ['cash', 'tips', locationId, date] as const,
  tipCandidates: (locationId: string, date: string) => ['cash', 'tip-candidates', locationId, date] as const,
  /** Prefisso comune: una timbratura aggiorna presenze, registro e mance. */
  staffAll: (locationId: string) => ['staff', locationId] as const,
  myWork: (locationId: string) => ['staff', locationId, 'me'] as const,
  attendanceRegister: (locationId: string, date: string) => ['staff', locationId, 'register', date] as const,
  staffDirectory: (locationId: string, includeInactive: boolean) =>
    ['staff', locationId, 'directory', includeInactive] as const,
  shifts: (locationId: string, weekStart: string) => ['staff', locationId, 'shifts', weekStart] as const,
  tipsByStaff: (locationId: string, from: string, to: string) => ['staff', locationId, 'tips', from, to] as const,
  inventory: (locationId: string) => ['inventory', locationId] as const,
  inventoryMovements: (locationId: string) => ['inventory', 'movements', locationId] as const,
  stockSyncIssues: (locationId: string) => ['inventory', 'issues', locationId] as const,
  recipeOverview: (organizationId: string) => ['recipes', 'overview', organizationId] as const,
  productRecipe: (productId: string) => ['recipes', 'product', productId] as const,
  ingredientOptions: (organizationId: string) => ['recipes', 'options', organizationId] as const,
  suppliers: (organizationId: string) => ['suppliers', organizationId] as const,
  purchaseOrders: (locationId: string) => ['purchase-orders', locationId] as const,
  recipes: (organizationId: string) => ['recipes', organizationId] as const,
  margins: (organizationId: string) => ['margins', organizationId] as const,
  salesDaily: (locationId: string, from: string, to: string) => ['sales', locationId, from, to] as const,
  salesByProduct: (locationId: string, from: string, to: string) =>
    ['sales', 'product', locationId, from, to] as const,
  salesByHour: (locationId: string, from: string, to: string) =>
    ['sales', 'hour', locationId, from, to] as const,
  payments: (locationId: string, from: string, to: string) => ['payments', locationId, from, to] as const,
  vat: (locationId: string, from: string, to: string) => ['vat', locationId, from, to] as const,
  reservationsReport: (locationId: string, from: string, to: string) =>
    ['reservations', 'report', locationId, from, to] as const,
} as const;

/** Rifiuti generati da PostgreSQL o formulati per chi sviluppa, non per l'operatore. */
function isTechnicalDenial(message: string): boolean {
  return /row-level security|permission denied|^Permesso [a-z_.]+ richiesto/i.test(message);
}

/** Traduce un errore del gateway in un messaggio comprensibile all'operatore. */
export function describeError(error: unknown): { title: string; message: string; hint?: string } {
  if (error instanceof DataError) {
    if (error.isForbidden) {
      return {
        title: 'Operazione non consentita',
        // I rifiuti scritti per l'operatore (es. «Non puoi cambiare il tuo
        // ruolo») spiegano più del messaggio generico; quelli tecnici no.
        message: isTechnicalDenial(error.message)
          ? 'Il tuo ruolo non prevede questa operazione.'
          : error.message,
        hint: 'Chiedi a un responsabile di eseguirla o di modificare i tuoi permessi.',
      };
    }
    if (error.isConcurrencyConflict) {
      return {
        title: 'Modifica concorrente',
        message: 'Un altro utente ha modificato questi dati mentre lavoravi.',
        hint: 'Ricarica la schermata e ripeti l’operazione.',
      };
    }
    if (error.isOverlap) {
      return {
        title: 'Sovrapposizione',
        message: error.message,
        hint: error.hint,
      };
    }
    if (error.isConflict) {
      return {
        title: 'Operazione già registrata',
        message: 'Questa operazione risulta già eseguita e non è stata duplicata.',
      };
    }
    return { title: 'Operazione non riuscita', message: error.message, hint: error.hint };
  }

  if (error instanceof Error) {
    return { title: 'Errore imprevisto', message: error.message };
  }
  return { title: 'Errore imprevisto', message: 'Si è verificato un problema non identificato.' };
}
